import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, InputGroup, Spinner } from 'react-bootstrap';

export type AudioSourceType = 'youtube' | 'mp3';

export const YT_ID_RE = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Accepts either a bare YouTube video ID or any common YouTube URL form
 * (watch?v=, youtu.be/, shorts/, embed/, with or without scheme/host prefix)
 * and returns the 11-character video ID. Falls back to the trimmed input
 * unchanged when nothing recognisable is found.
 */
export function extractYoutubeId(input: string): string {
  const raw = input.trim();
  if (!raw) return '';
  if (YT_ID_RE.test(raw)) return raw;

  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0];
      if (id && YT_ID_RE.test(id)) return id;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      const v = url.searchParams.get('v');
      if (v && YT_ID_RE.test(v)) return v;
      const parts = url.pathname.split('/').filter(Boolean);
      if (parts.length >= 2 && /^(shorts|embed|v|live)$/i.test(parts[0])) {
        if (YT_ID_RE.test(parts[1])) return parts[1];
      }
    }
  } catch {
    /* not a URL – fall through */
  }
  return raw;
}

async function checkYoutubeId(id: string): Promise<{ valid: boolean; title?: string }> {
  try {
    const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${id}&format=json`;
    const res = await fetch(url);
    if (!res.ok) return { valid: false };
    const data = await res.json();
    return { valid: true, title: data.title as string };
  } catch {
    return { valid: false };
  }
}

type YoutubeValidity = 'idle' | 'checking' | 'valid' | 'invalid';

export interface AudioSourceValue {
  audioSourceType: AudioSourceType;
  youtubeId: string;
  audioFile: string;
  bpm: number;
}

export interface AudioSourcePickerProps {
  value: AudioSourceValue;
  onChange: (patch: Partial<AudioSourceValue>) => void;
  /** Whether a blob for the current track is already stored in IndexedDB. */
  hasLocalFile: boolean;
  /** File picked in this session but not yet persisted. */
  pendingFile: File | null;
  /** Called when the user picks a new local MP3 file (or clears it). */
  onFilePicked: (file: File | null) => void;
  /** Called whenever the resolved YouTube title changes (null when unknown/invalid). */
  onYoutubeTitleChange?: (title: string | null) => void;
}

const AudioSourcePicker: React.FC<AudioSourcePickerProps> = ({
  value, onChange, hasLocalFile, pendingFile, onFilePicked, onYoutubeTitleChange,
}) => {
  const { audioSourceType, youtubeId, audioFile, bpm } = value;

  const [ytValidity, setYtValidity] = useState<YoutubeValidity>('idle');
  const [ytTitle, setYtTitle] = useState<string | null>(null);
  const ytDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    onYoutubeTitleChange?.(ytTitle);
  }, [ytTitle, onYoutubeTitleChange]);

  useEffect(() => {
    if (audioSourceType !== 'youtube') {
      setYtValidity('idle');
      setYtTitle(null);
      if (ytDebounceRef.current) clearTimeout(ytDebounceRef.current);
      return;
    }
    const id = youtubeId.trim();
    if (ytDebounceRef.current) clearTimeout(ytDebounceRef.current);
    if (!id) { setYtValidity('idle'); setYtTitle(null); return; }
    if (!YT_ID_RE.test(id)) { setYtValidity('invalid'); setYtTitle(null); return; }
    setYtValidity('checking');
    setYtTitle(null);
    ytDebounceRef.current = setTimeout(() => {
      checkYoutubeId(id).then(({ valid, title }) => {
        setYtValidity(valid ? 'valid' : 'invalid');
        setYtTitle(valid && title ? title : null);
      });
    }, 600);
    return () => { if (ytDebounceRef.current) clearTimeout(ytDebounceRef.current); };
  }, [youtubeId, audioSourceType]);

  return (
    <Form.Group className="mb-4">
      <Form.Label>Audio Source</Form.Label>
      <div className="d-flex gap-4 mb-3">
        <Form.Check
          type="radio"
          id="src-youtube"
          label="YouTube"
          checked={audioSourceType === 'youtube'}
          onChange={() => onChange({ audioSourceType: 'youtube' })}
        />
        <Form.Check
          type="radio"
          id="src-mp3"
          label="Local MP3"
          checked={audioSourceType === 'mp3'}
          onChange={() => onChange({ audioSourceType: 'mp3' })}
        />
      </div>

      {audioSourceType === 'youtube' && (
        <>
          <div className="d-flex align-items-center justify-content-between mb-1">
            <Form.Label className="text-muted small mb-0">YouTube Video ID</Form.Label>
            <Alert variant="info">
                Tip: you can paste a full YouTube link
            </Alert>
          </div>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Video ID or YouTube URL"
              value={youtubeId}
              onChange={(e) => onChange({ youtubeId: extractYoutubeId(e.target.value) })}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData('text');
                const id = extractYoutubeId(pasted);
                if (id !== pasted.trim()) {
                  e.preventDefault();
                  onChange({ youtubeId: id });
                }
              }}
              isValid={ytValidity === 'valid'}
              isInvalid={ytValidity === 'invalid'}
            />
            {ytValidity === 'checking' && (
              <InputGroup.Text><Spinner size="sm" /></InputGroup.Text>
            )}
            {ytValidity === 'valid' && (
              <InputGroup.Text className="text-success">✔</InputGroup.Text>
            )}
            {ytValidity === 'invalid' && (
              <InputGroup.Text className="text-danger">✖</InputGroup.Text>
            )}
          </InputGroup>
          <Form.Text className={ytValidity === 'invalid' ? 'text-danger' : 'text-muted'}>
            {ytValidity === 'invalid'
              ? (YT_ID_RE.test(youtubeId.trim())
                  ? 'Video not found or not embeddable'
                  : 'Must be 11 characters (letters, numbers, - and _)')
              : ytTitle
              ? <strong className="text-success">{ytTitle}</strong>
              : 'The video ID from a YouTube URL (youtube.com/watch?v=ID)'}
          </Form.Text>
        </>
      )}

      {audioSourceType === 'mp3' && (
        <>
          <Form.Label className="text-muted small mb-1">MP3 File</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="MySong.mp3"
              value={audioFile}
              onChange={(e) => { onChange({ audioFile: e.target.value }); onFilePicked(null); }}
            />
            <Button
              variant="outline-secondary"
              as="label"
              style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Browse…
              <input
                type="file"
                accept=".mp3,audio/*"
                hidden
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    onFilePicked(file);
                    onChange({ audioFile: file.name });
                  }
                }}
              />
            </Button>
          </InputGroup>
          <div className="mt-1">
            {pendingFile ? (
              <Form.Text className="text-warning">
                ⚠ File selected — save the track to store it on this device.
              </Form.Text>
            ) : hasLocalFile ? (
              <Form.Text className="text-success">
                <Badge bg="success" className="me-1">✔ Stored on this device</Badge>
                File is stored locally and will need to be specified separately to work other devices.
              </Form.Text>
            ) : (
              <Form.Text className="text-muted">
                Pick a file — it will be stored in your browser for this device.
              </Form.Text>
            )}
          </div>
        </>
      )}

      <div className="mt-3">
        <Form.Label className="text-muted small mb-1">BPM</Form.Label>
        <Form.Control
          type="number"
          value={bpm}
          min={20}
          max={400}
          onChange={(e) => onChange({ bpm: parseInt(e.target.value, 10) || 120 })}
        />
      </div>
    </Form.Group>
  );
};

export default AudioSourcePicker;
